import React, { useContext, useEffect, useState, FC } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { VerticalContext } from 'store/verticals/Context'
import ActivityOverviewCard from 'containers/VerticalPage/nft/ActivityOverviewCard/ActivityOverviewCard'
import VerticalCard from 'containers/VerticalPage/common/VerticalCard/VerticalCard'
import RevenueSourcesOverview from 'containers/VerticalPage/common/RevenueSourcesOverview/RevenueSourcesOverview'
import EntityCard from 'components/EntityCard/EntityCard'
import GenericCarousel from 'components/common/GenericCarousel'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import {
  ActivityOverviewCardLoader,
  EntityCardLoader,
  VerticalCarouselLoader,
} from 'containers/VerticalPage/common/Loaders'
import { selectCurrentZone } from 'store/global'
import type { CurrentZone } from 'utils/types'
import { PLATFORMS } from 'store/verticals/types'
import { useTheme } from '@mui/material/styles'
import { originalSourceToImageMap } from 'components/BusinessSource/specs'

const Content: FC = () => {
  const { entityId } = useParams<{ entityId: string }>()
  const { getVerticalEntity, verticalEntity, isCollapsedRevenuesCarousel, toggleRevenuesCarousel } =
    useContext(VerticalContext)
  const [isLoading, setIsLoading] = useState(false)
  const currentZone: CurrentZone = useSelector(selectCurrentZone)
  const theme = useTheme()

  useEffect(() => {
    if (!entityId || !currentZone) return

    if (currentZone.cryptoIndices) {
      const fetchPersonCardData = async () => {
        setIsLoading(true)
        await getVerticalEntity(entityId, currentZone.cryptoIndices)
        setIsLoading(false)
      }
      fetchPersonCardData()
    }
  }, [currentZone, entityId])

  const handleToggleCards = () => {
    toggleRevenuesCarousel()
  }

  const cardConfig: Partial<
    Record<
      PLATFORMS,
      {
        revenueSourceFields: string[]
        cardHeaderBgColor?: string
        cardHeadingLinkColor?: string
        platformImg?: string
      }
    >
  > = {
    [PLATFORMS.SMART_CONTRACT]: {
      revenueSourceFields: ['chains', 'inflows', 'outflows', 'firstActivity'],
      cardHeaderBgColor: theme.palette.platforms.smartcontract,
      cardHeadingLinkColor: theme.palette.textColor.main,
      platformImg: originalSourceToImageMap['smartcontract'],
    },
    [PLATFORMS.WALLET]: {
      revenueSourceFields: ['chains', 'inflows', 'outflows', 'firstActivity'],
      cardHeaderBgColor: theme.palette.platforms.wallet,
      cardHeadingLinkColor: theme.palette.textColor.main,
      platformImg: originalSourceToImageMap['wallet'],
    },
    [PLATFORMS.ETHEREUM]: {
      revenueSourceFields: ['chains', 'inflows', 'outflows', 'firstActivity'],
      cardHeaderBgColor: theme.palette.platforms.ethereum,
      cardHeadingLinkColor: theme.palette.textColor.main,
      platformImg: originalSourceToImageMap['ethereum'],
    },
  }

  const renderCarouselCards = () => {
    return verticalEntity?.revenueSources?.length ? (
      verticalEntity?.revenueSources?.map(source => {
        let currentConfig
        let profileName = source.profileName
        if (source.overview.alias) {
          profileName = source.overview.alias
        }
        switch (source?.overview?.type?.toLowerCase()) {
          case 'wallet':
            currentConfig = cardConfig[PLATFORMS.WALLET]
            break
          case 'smart contract':
            currentConfig = cardConfig[PLATFORMS.SMART_CONTRACT]
            break
          default:
            currentConfig = cardConfig[PLATFORMS.ETHEREUM]
            break
        }

        return (
          <Stack
            direction='row'
            key={source.profileUrl}
            sx={{
              justifyContent: 'space-between',
              mb: '0',
            }}
          >
            <VerticalCard
              platform={source.platform}
              profileName={profileName}
              profileImage={source.profileImageUrl}
              profileUrl={source.profileUrl}
              associates={source.associates}
              areAllCardsCollapsed={isCollapsedRevenuesCarousel}
              handleToggleCards={handleToggleCards}
              isCollapsible={true}
              cardHeaderBgColor={currentConfig?.cardHeaderBgColor}
              cardHeadingLinkColor={currentConfig?.cardHeadingLinkColor}
              customPlatformImg={currentConfig?.platformImg}
            >
              <RevenueSourcesOverview
                revenueSources={source.overview}
                includeFields={currentConfig?.revenueSourceFields}
                customOrder={currentConfig?.revenueSourceFields}
              />
            </VerticalCard>
          </Stack>
        )
      })
    ) : (
      <VerticalCarouselLoader />
    )
  }

  return (
    <Box
      sx={{
        gridColumn: '1 / 2',
        gridRow: '1 / 2',
        display: 'grid',
        gridTemplateColumns: '32% minmax(0, 1fr)',
        gridTemplateRows: 'auto',
        gap: { md: '12px 18px', lg: '12px 22px', xl: '18px 25px', xxl: '28px', xxxl: '30px' },
      }}
    >
      {verticalEntity?.entityDetails ? (
        <EntityCard
          tin={verticalEntity.entityDetails.tin}
          name={verticalEntity.entityDetails.name}
          image={verticalEntity.entityDetails.image}
          profileUrls={verticalEntity.entityDetails.profileUrls}
          location={verticalEntity.entityDetails.location}
          birthday={verticalEntity.entityDetails.birthday}
        />
      ) : (
        isLoading && <EntityCardLoader />
      )}

      {verticalEntity?.overviewDetails ? (
        <ActivityOverviewCard
          overviewData={{
            ...verticalEntity.overviewDetails,
            profitLoss: verticalEntity.totals.profitLoss,
          }}
          documentedBuys={verticalEntity.totals.documentedBuys}
        />
      ) : (
        isLoading && <ActivityOverviewCardLoader />
      )}

      {verticalEntity ? (
        <GenericCarousel
          slidesCount={verticalEntity?.revenueSources?.length || 0}
          isCollapsible
          isCollapsedCarousel={isCollapsedRevenuesCarousel}
          setIsCollapsedCarousel={toggleRevenuesCarousel}
        >
          {renderCarouselCards()}
        </GenericCarousel>
      ) : (
        isLoading && <VerticalCarouselLoader />
      )}
    </Box>
  )
}

export default Content
